import type { Node } from 'reactflow';
import type { BoardDrawKind, BoardDrawPoint, BoardDrawing, BoardImageData } from '../../types';
import { calloutBoxPx } from '../pdf/engineeringMarkup';
import { CALLOUT_STROKE, DEFAULT_NODE_SIZE, IMAGE_CAPTION_HEIGHT } from './boardConstants';

/** Two-letter initials from a display name or email. */
export function initialsFromName(name: string): string {
  const cleaned = (name || '').trim();
  if (!cleaned) return '';
  const base = cleaned.includes('@') ? cleaned.split('@')[0] : cleaned;
  const parts = base.replace(/[._-]+/g, ' ').split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
  }
  return base.slice(0, 2).toUpperCase();
}

export function calloutBoxPoint(d: BoardDrawing): BoardDrawPoint | undefined {
  return d.points[d.points.length - 1] || d.points[0];
}

/** True when the drawing lives in 0–1 image-local space. */
export function isNormalizedDrawing(d: BoardDrawing): boolean {
  return d.points.length > 0 && d.points.every((p) => p.x <= 1.5 && p.y <= 1.5 && p.x >= -0.1 && p.y >= -0.1);
}

/** Callout label box size in the drawing's coordinate space. */
export function calloutBoxSize(d: BoardDrawing, normalized?: boolean): { w: number; h: number } {
  const initials = (d.authorInitials || '').trim();
  const text = (d.text || '').trim();
  const sized = calloutBoxPx(text || (initials ? initials : 'Note'));
  const w = Math.max(sized.w, initials ? 72 : sized.w);
  const h = sized.h;
  const norm = normalized ?? isNormalizedDrawing(d);
  return norm ? { w: w / 280, h: h / 280 } : { w, h };
}

export function moveDrawingPoint(d: BoardDrawing, index: number, to: BoardDrawPoint): BoardDrawing {
  return {
    ...d,
    points: d.points.map((p, i) => (i === index ? { x: to.x, y: to.y } : p)),
  };
}

/** Pin, elbow, or box vertex under the pointer. */
export function calloutVertexAtPoint(
  d: BoardDrawing,
  point: BoardDrawPoint,
  threshold: number,
): number | null {
  if (d.kind !== 'callout') return null;
  for (let i = 0; i < d.points.length; i += 1) {
    const p = d.points[i];
    if (Math.hypot(point.x - p.x, point.y - p.y) <= threshold) return i;
  }
  return null;
}

export function calloutPartAtPoint(
  d: BoardDrawing,
  point: BoardDrawPoint,
  threshold: number,
): 'box' | 'pin' | 'leader' | number | null {
  if (d.kind !== 'callout') return null;
  const vertex = calloutVertexAtPoint(d, point, threshold);
  if (vertex != null) return vertex;
  const box = calloutBoxPoint(d);
  if (box) {
    const { w, h } = calloutBoxSize(d);
    if (Math.abs(point.x - box.x) <= w / 2 && Math.abs(point.y - box.y) <= h / 2) return 'box';
  }
  const pin = d.points[0];
  if (pin && Math.hypot(point.x - pin.x, point.y - pin.y) <= threshold * 1.6) return 'pin';
  if (strokeHitsPoint(d, point, threshold)) return 'leader';
  return null;
}

export interface FlowRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type BoardImageRotation = 0 | 90 | 180 | 270;

export function normalizeImageRotation(value?: number): BoardImageRotation {
  const stepped = ((((Math.round((value || 0) / 90) * 90) % 360) + 360) % 360) as BoardImageRotation;
  return stepped === 90 || stepped === 180 || stepped === 270 ? stepped : 0;
}

export function nextImageRotation(value?: number): BoardImageRotation {
  return normalizeImageRotation((normalizeImageRotation(value) + 90) % 360);
}

/** Photo opacity: 5%–100%, default 100%. */
export function normalizeImageOpacity(value?: number): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return 1;
  const stepped = Math.round(value * 100) / 100;
  return Math.min(1, Math.max(0.05, stepped));
}

/** 90° clockwise in 0–1 image space: (x, y) → (1 − y, x). */
export function rotateNormPointCW(p: BoardDrawPoint): BoardDrawPoint {
  return { x: 1 - p.y, y: p.x };
}

export function rotateImageDrawingsCW(drawings: BoardDrawing[] | undefined): BoardDrawing[] | undefined {
  if (!drawings?.length) return drawings;
  return drawings.map((d) => ({
    ...d,
    points: d.points.map(rotateNormPointCW),
  }));
}

export function genDrawId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `draw-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function nodeSize(n: Node): { width: number; height: number } {
  const defaults = DEFAULT_NODE_SIZE[(n.type || 'image') as keyof typeof DEFAULT_NODE_SIZE] || {
    width: 200,
    height: 200,
  };
  return {
    width: Number(n.width ?? n.style?.width ?? defaults.width),
    height: Number(n.height ?? n.style?.height ?? defaults.height),
  };
}

/** Set an image caption and grow/shrink the node so the photo size stays put. */
export function withImageCaption(node: Node, caption: string | undefined): Node {
  const next = (caption || '').trim() || undefined;
  const prev = String((node.data as BoardImageData | undefined)?.caption || '').trim() || undefined;
  const data = { ...node.data, caption: next };
  if (node.type !== 'image' || !!prev === !!next) {
    return { ...node, data };
  }
  const { width, height } = nodeSize(node);
  const nextH = Math.max(60, height + (next ? IMAGE_CAPTION_HEIGHT : -IMAGE_CAPTION_HEIGHT));
  return {
    ...node,
    height: nextH,
    style: { ...(node.style || {}), width: Number(node.style?.width ?? width), height: nextH },
    data,
  };
}

export function absolutePosition(node: Node, nodes: Node[]): BoardDrawPoint {
  let x = node.position.x;
  let y = node.position.y;
  let parentId = node.parentId;
  while (parentId) {
    const parent = nodes.find((n) => n.id === parentId);
    if (!parent) break;
    x += parent.position.x;
    y += parent.position.y;
    parentId = parent.parentId;
  }
  return { x, y };
}

/** Image media box in flow coordinates (excludes caption strip). */
export function imageMediaRect(node: Node, nodes: Node[]): FlowRect | null {
  if (node.type !== 'image') return null;
  const abs = absolutePosition(node, nodes);
  const { width, height } = nodeSize(node);
  const captionH = (node.data as BoardImageData)?.caption ? IMAGE_CAPTION_HEIGHT : 0;
  return {
    x: abs.x,
    y: abs.y,
    width,
    height: Math.max(8, height - captionH),
  };
}

export function pointInRect(p: BoardDrawPoint, r: FlowRect): boolean {
  return p.x >= r.x && p.y >= r.y && p.x <= r.x + r.width && p.y <= r.y + r.height;
}

export function hitTestImage(
  flowPoint: BoardDrawPoint,
  nodes: Node[],
): { nodeId: string; media: FlowRect } | null {
  const images = nodes.filter((n) => n.type === 'image');
  // Top-most last in DOM is highest z; walk from the end.
  for (let i = images.length - 1; i >= 0; i -= 1) {
    const media = imageMediaRect(images[i], nodes);
    if (media && pointInRect(flowPoint, media)) {
      return { nodeId: images[i].id, media };
    }
  }
  return null;
}

export function toImageNorm(p: BoardDrawPoint, media: FlowRect): BoardDrawPoint {
  return {
    x: (p.x - media.x) / media.width,
    y: (p.y - media.y) / media.height,
  };
}

export function fromImageNorm(p: BoardDrawPoint, media: FlowRect): BoardDrawPoint {
  return {
    x: media.x + p.x * media.width,
    y: media.y + p.y * media.height,
  };
}

/** Image-local callouts projected into flow space so they can sit outside the photo. */
export function imageCalloutsInFlow(
  nodes: Node[],
): { flow: BoardDrawing; orig: BoardDrawing; target: string }[] {
  const items: { flow: BoardDrawing; orig: BoardDrawing; target: string }[] = [];
  for (const node of nodes) {
    if (node.type !== 'image') continue;
    const media = imageMediaRect(node, nodes);
    if (!media) continue;
    for (const d of ((node.data as BoardImageData).drawings || [])) {
      if (d.kind !== 'callout') continue;
      items.push({
        orig: d,
        target: node.id,
        flow: {
          ...d,
          width: CALLOUT_STROKE,
          points: d.points.map((p) => fromImageNorm(p, media)),
        },
      });
    }
  }
  return items;
}

/** Lock a point to 0 / 45 / 90 degrees from `from`. */
export function snapAngle(from: BoardDrawPoint, to: BoardDrawPoint): BoardDrawPoint {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (dx === 0 && dy === 0) return to;
  const angle = Math.atan2(dy, dx);
  const snapped = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
  const len = Math.hypot(dx, dy);
  return {
    x: from.x + Math.cos(snapped) * len,
    y: from.y + Math.sin(snapped) * len,
  };
}

/** Force a two-point shape to a square / circle by matching the dominant axis. */
export function constrainSquare(from: BoardDrawPoint, to: BoardDrawPoint): BoardDrawPoint {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const side = Math.max(Math.abs(dx), Math.abs(dy));
  return {
    x: from.x + Math.sign(dx || 1) * side,
    y: from.y + Math.sign(dy || 1) * side,
  };
}

export function constrainEnd(
  kind: BoardDrawKind,
  from: BoardDrawPoint,
  to: BoardDrawPoint,
  shift: boolean,
): BoardDrawPoint {
  if (!shift) return to;
  if (kind === 'rect' || kind === 'ellipse') return constrainSquare(from, to);
  if (kind === 'pen' || kind === 'highlighter' || kind === 'line' || kind === 'arrow' || kind === 'callout') {
    return snapAngle(from, to);
  }
  return to;
}

function dist2(a: BoardDrawPoint, b: BoardDrawPoint): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

function distToSegment(p: BoardDrawPoint, a: BoardDrawPoint, b: BoardDrawPoint): number {
  const l2 = dist2(a, b);
  if (l2 === 0) return Math.sqrt(dist2(p, a));
  let t = ((p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y)) / l2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * (b.x - a.x)), p.y - (a.y + t * (b.y - a.y)));
}

function shapeSegments(d: BoardDrawing): [BoardDrawPoint, BoardDrawPoint][] {
  const pts = d.points;
  if (pts.length < 2) return [];
  if (d.kind === 'pen' || d.kind === 'highlighter') {
    const segs: [BoardDrawPoint, BoardDrawPoint][] = [];
    for (let i = 1; i < pts.length; i += 1) segs.push([pts[i - 1], pts[i]]);
    return segs;
  }
  if (d.kind === 'line' || d.kind === 'arrow') {
    return [[pts[0], pts[1]]];
  }
  if (d.kind === 'callout') {
    const segs: [BoardDrawPoint, BoardDrawPoint][] = [];
    for (let i = 1; i < pts.length; i += 1) segs.push([pts[i - 1], pts[i]]);
    return segs;
  }
  // rect / ellipse — test the bounding box edges
  const x1 = Math.min(pts[0].x, pts[1].x);
  const y1 = Math.min(pts[0].y, pts[1].y);
  const x2 = Math.max(pts[0].x, pts[1].x);
  const y2 = Math.max(pts[0].y, pts[1].y);
  return [
    [{ x: x1, y: y1 }, { x: x2, y: y1 }],
    [{ x: x2, y: y1 }, { x: x2, y: y2 }],
    [{ x: x2, y: y2 }, { x: x1, y: y2 }],
    [{ x: x1, y: y2 }, { x: x1, y: y1 }],
  ];
}

export function drawingBounds(d: BoardDrawing): FlowRect {
  const xs = d.points.map((p) => p.x);
  const ys = d.points.map((p) => p.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return {
    x,
    y,
    width: Math.max(0.001, Math.max(...xs) - x),
    height: Math.max(0.001, Math.max(...ys) - y),
  };
}

/** Interior + stroke hit — used for selecting boxes, not just their outlines. */
export function drawingHitsPoint(d: BoardDrawing, point: BoardDrawPoint, threshold: number): boolean {
  if (d.kind === 'rect' || d.kind === 'ellipse') {
    const b = drawingBounds(d);
    return (
      point.x >= b.x - threshold &&
      point.x <= b.x + b.width + threshold &&
      point.y >= b.y - threshold &&
      point.y <= b.y + b.height + threshold
    );
  }
  return strokeHitsPoint(d, point, threshold);
}

export function moveDrawing(d: BoardDrawing, dx: number, dy: number): BoardDrawing {
  return {
    ...d,
    points: d.points.map((p) => ({ x: p.x + dx, y: p.y + dy })),
  };
}

export type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se';

export function resizeDrawing(
  d: BoardDrawing,
  handle: ResizeHandle,
  to: BoardDrawPoint,
  shift: boolean,
): BoardDrawing {
  const b = drawingBounds(d);
  let x1 = b.x;
  let y1 = b.y;
  let x2 = b.x + b.width;
  let y2 = b.y + b.height;
  if (handle === 'nw' || handle === 'sw') x1 = to.x;
  if (handle === 'ne' || handle === 'se') x2 = to.x;
  if (handle === 'nw' || handle === 'ne') y1 = to.y;
  if (handle === 'sw' || handle === 'se') y2 = to.y;
  if (shift) {
    const side = Math.max(Math.abs(x2 - x1), Math.abs(y2 - y1));
    if (handle === 'nw' || handle === 'sw') x1 = x2 - Math.sign(x2 - x1 || 1) * side;
    else x2 = x1 + Math.sign(x2 - x1 || 1) * side;
    if (handle === 'nw' || handle === 'ne') y1 = y2 - Math.sign(y2 - y1 || 1) * side;
    else y2 = y1 + Math.sign(y2 - y1 || 1) * side;
  }
  const min = d.points[0].x <= 1 && d.points[0].y <= 1 ? 0.02 : 8;
  if (Math.abs(x2 - x1) < min) x2 = x1 + (x2 >= x1 ? min : -min);
  if (Math.abs(y2 - y1) < min) y2 = y1 + (y2 >= y1 ? min : -min);
  return { ...d, points: [{ x: x1, y: y1 }, { x: x2, y: y2 }] };
}

/** True when `point` is within `threshold` of the stroke (same coordinate space as the drawing). */
export function strokeHitsPoint(d: BoardDrawing, point: BoardDrawPoint, threshold: number): boolean {
  const pad = threshold + (d.width || 0) / 2;
  for (const [a, b] of shapeSegments(d)) {
    if (distToSegment(point, a, b) <= pad) return true;
  }
  if (d.kind === 'callout') {
    const box = calloutBoxPoint(d);
    if (box) {
      const { w, h } = calloutBoxSize(d);
      if (Math.abs(point.x - box.x) <= w / 2 + pad && Math.abs(point.y - box.y) <= h / 2 + pad) {
        return true;
      }
    }
  }
  return false;
}

export function drawingsSignature(drawings: BoardDrawing[]): string {
  return drawings
    .map(
      (d) =>
        `${d.id}|${d.kind}|${d.color}|${d.width}|${d.opacity ?? ''}|${d.text ?? ''}|${d.authorInitials ?? ''}|${d.zIndex ?? ''}|${d.points
          .map((p) => `${p.x.toFixed(3)},${p.y.toFixed(3)}`)
          .join(';')}`,
    )
    .sort()
    .join('§');
}

const LEGACY_DRAWING_Z = 1_000_000;

export function drawingZ(d: BoardDrawing): number {
  return typeof d.zIndex === 'number' ? d.zIndex : LEGACY_DRAWING_Z;
}

export function sortDrawings<T extends BoardDrawing>(drawings: T[]): T[] {
  return drawings
    .map((d, i) => ({ d, i }))
    .sort((a, b) => {
      const dz = drawingZ(a.d) - drawingZ(b.d);
      return dz !== 0 ? dz : a.i - b.i;
    })
    .map(({ d }) => d);
}

export function nodeFlowRect(node: Node, nodes: Node[]): FlowRect {
  const pos = absolutePosition(node, nodes);
  const size = nodeSize(node);
  return { x: pos.x, y: pos.y, width: size.width, height: size.height };
}

export function maxNodeZIndex(nodes: Node[]): number {
  if (nodes.length === 0) return 0;
  return Math.max(...nodes.map((n) => (typeof n.zIndex === 'number' ? n.zIndex : 0)));
}

/** Frames stay under images/text no matter what z-index they were saved with. */
export function isFrameDrawing(d: Pick<BoardDrawing, 'kind'>): boolean {
  return d.kind === 'rect';
}

/** Callouts stay above images unless the user explicitly sent them back. Frames never do. */
export function drawingIsFront(d: BoardDrawing, maxZ: number): boolean {
  if (isFrameDrawing(d)) return false;
  if (d.kind === 'callout' && typeof d.zIndex !== 'number') return true;
  return (typeof d.zIndex === 'number' ? d.zIndex : Number.POSITIVE_INFINITY) > maxZ;
}

export type StackHit =
  | { kind: 'drawing'; drawing: BoardDrawing; target: 'canvas' | string }
  | { kind: 'node'; id: string };

/** Topmost node or canvas drawing at a flow point. Frames lose to any node, including groups. */
export function topmostAtPoint(
  point: BoardDrawPoint,
  nodes: Node[],
  drawings: BoardDrawing[],
  threshold: number,
  drawingTarget: (d: BoardDrawing) => 'canvas' | string = () => 'canvas',
): StackHit | null {
  const hits: Array<StackHit & { z: number }> = [];
  for (const n of nodes) {
    if (pointInRect(point, nodeFlowRect(n, nodes))) {
      hits.push({ kind: 'node', id: n.id, z: typeof n.zIndex === 'number' ? n.zIndex : 0 });
    }
  }
  for (const d of drawings) {
    if (drawingHitsPoint(d, point, threshold)) {
      hits.push({ kind: 'drawing', drawing: d, target: drawingTarget(d), z: drawingZ(d) });
    }
  }
  const nodeHit = hits.some((h) => h.kind === 'node');
  const ranked = nodeHit
    ? hits.filter((h) => h.kind === 'node' || (h.kind === 'drawing' && !isFrameDrawing(h.drawing)))
    : hits;
  ranked.sort((a, b) => b.z - a.z);
  return ranked[0] || null;
}
